import {
  ClassTypeWithPropertyType,
  PropertyType,
} from "@/tailwind/tailwindUtils";
import { ComboBoxOptionType } from "../components/input";
import { classMatchedPropertyType } from "../../usePropertyStateV2";

export const safeParseCssStringAsInlineStyles = (cssString) => {
  try {
    const cssObject = {};
    const lines = cssString.split(";");

    lines.forEach((line) => {
      const [key, value] = line.split(":");
      if (key && value) {
        const formattedKey = key
          .trim()
          .replace(/-./g, (match) => match[1].toUpperCase());
        cssObject[formattedKey] = value.trim();
      }
    });
    return cssObject;
  } catch (error) {
    return {};
  }
};

const justifyContentMap = ["flex-start", "center", "flex-end"];
const alignItemsMap = ["flex-start", "center", "flex-end"];

export const modifyForReverse = (direction: string, alignment: string) => {
  if (direction?.endsWith("reverse")) {
    const reverseMap = {
      "flex-start": "flex-end",
      "flex-end": "flex-start",
      center: "center",
    };
    return reverseMap[alignment];
  }
  return alignment;
};

export const getIndexFromStyles = (
  direction: string,
  justifyContent: string,
  alignItems: string
) => {
  if (alignItems === "start") {
    alignItems = "flex-start";
  } else if (alignItems === "end") {
    alignItems = "flex-end";
  }

  if (justifyContent === "start") {
    justifyContent = "flex-start";
  } else if (justifyContent === "end") {
    justifyContent = "flex-end";
  }

  justifyContent = modifyForReverse(direction, justifyContent);

  const justifyContentIndex = justifyContentMap.indexOf(justifyContent);
  const alignItemsIndex = alignItemsMap.indexOf(alignItems);

  if (justifyContentIndex === -1 || alignItemsIndex === -1) {
    return -1;
  }

  let index;
  if (!direction || direction.startsWith("row")) {
    index = alignItemsIndex * 3 + justifyContentIndex;
  } else {
    index = justifyContentIndex * 3 + alignItemsIndex;
  }

  return index;
};

export const getStylesFromIndex = (direction: string, index: number) => {
  let justifyContent: string, alignItems: string;

  if (direction.startsWith("row")) {
    justifyContent = justifyContentMap[index % 3];
    alignItems = alignItemsMap[Math.floor(index / 3)];
  } else {
    justifyContent = justifyContentMap[Math.floor(index / 3)];
    alignItems = alignItemsMap[index % 3];
  }

  justifyContent = modifyForReverse(direction, justifyContent);

  return { justifyContent, alignItems };
};

export const calculateNewClasses = ({
  option,
  propertyType,
  oldClasses,
  tailwindClassesMap,
  variant,
}: {
  option: ComboBoxOptionType | null;
  propertyType: PropertyType;
  oldClasses: string[];
  tailwindClassesMap: Record<string, ClassTypeWithPropertyType>;
  variant?: string | null;
}) => {
  const newClasses: string[] = [];
  let found = false;

  for (let i = 0; i < oldClasses.length; i++) {
    const className = oldClasses[i];

    if (
      classMatchedPropertyType({
        className,
        propertyType,
        tailwindClassMap: tailwindClassesMap,
        variant,
      })
    ) {
      if (!found) {
        found = true;
        if (option) {
          if (variant && variant !== "default") {
            newClasses.push(`${variant}:${option.tailwind}`);
          } else {
            newClasses.push(option.tailwind);
          }
        }
      } else {
        continue;
      }
    } else {
      newClasses.push(className);
    }
  }

  if (!found) {
    if (option) {
      if (variant && variant !== "default") {
        newClasses.push(`${variant}:${option.tailwind}`);
      } else {
        newClasses.push(option.tailwind);
      }
    }
  }

  return newClasses;
};

export function sortTailwindColorOptions<T extends { tailwind: string }>(
  options: T[]
): T[] {
  return [...options].sort((a, b) => {
    const [aBase, aOpacity] = a.tailwind.split("/");
    const [bBase, bOpacity] = b.tailwind.split("/");
    if (aBase < bBase) return -1;
    if (aBase > bBase) return 1;

    const aOp = aOpacity ? parseInt(aOpacity) : 100;
    const bOp = bOpacity ? parseInt(bOpacity) : 100;

    return bOp - aOp;
  });
}
